/* eslint-disable react-hooks/exhaustive-deps */
import { faCirclePlus, faInfoCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { Accordion, Button, Card, Col, Form, Row } from "react-bootstrap";

import { ProfitsInLieuOfSalary } from "../../../../config/filingConstants";
import { IAddIncomeForm } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showHintModal } from "../../../../redux/slice/HintModalSlice";
import FASvgIcon from "../../../FASvgIcon";
import PreventEditComponent from "../../../PreventEditComponent";
import PriceDisplay from "../../../PriceDisplay";
import { ContextAwareToggle } from "../../../accordion";
import { NumericInputField, SelectInputField } from "../../../fields";

const SalarySection17ThreeForm = () => {
	const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<IAddIncomeForm>();

	const dispatch = useAppDispatch();
	const [disableInput, setDisableInput] = useState(false);

	return (
		<>
			<Accordion className="accordion-sm" defaultActiveKey="gross-salary-1-c">
				<Accordion.Item eventKey="gross-salary-1-c">
					<ContextAwareToggle eventKey="gross-salary-1-c" isChild={true}>
						<div className="btn-content justify-content-between">
							<Form.Label className="leading-para mb-0">
								I(c). Profits in lieu of Salary u/s 17(3)
								<span
									className="info-wrap"
									role="button"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										dispatch(
											showHintModal({
												title: "17(3) from Form 12BA",
												componentName: "ProfitsInLieu173Hint"
											})
										);
									}}
								>
									<FASvgIcon icon={faInfoCircle} />
								</span>
							</Form.Label>
							<div
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
								}}
								className="flex-grow-1"
							>
								<NumericInputField
									showLeftIcon={true}
									name="ProfitsInLieuOfSalaryUnderSection17_3"
									value={values?.ProfitsInLieuOfSalaryUnderSection17_3 || ""}
									onBlur={handleBlur("ProfitsInLieuOfSalaryUnderSection17_3")}
									onChange={(value) =>
										setFieldValue("ProfitsInLieuOfSalaryUnderSection17_3", value)
									}
									isInvalid={
										Boolean(errors.ProfitsInLieuOfSalaryUnderSection17_3) &&
										touched.ProfitsInLieuOfSalaryUnderSection17_3
									}
									isDisabled={disableInput}
								/>
							</div>
						</div>
					</ContextAwareToggle>
					<Accordion.Body className="py-0">
						<Section17ThreeBreakupForm handleOnSumChange={(val) => setDisableInput(() => val)} />
					</Accordion.Body>
				</Accordion.Item>
			</Accordion>
		</>
	);
};

export default SalarySection17ThreeForm;

type FormProps = { handleOnSumChange: (isDisabled: boolean) => void };

const Section17ThreeBreakupForm: React.FC<FormProps> = (props: FormProps) => {
	const { handleOnSumChange } = props;

	const { values, setFieldValue } = useFormikContext<IAddIncomeForm>();

	const [sum, setSum] = useState(0);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());

	useEffect(() => {
		let _sum = 0;
		values?.ProfitsInLieuDescriptions?.forEach((item) => {
			if (item.length && item[1]) {
				_sum += Number(item[1]) || 0;
				setIsFirstRender(() => false);
			}
		});
		setSum(() => _sum);

		// also update the gross total in upper section
		handleOnSumChange(_sum > 0);

		if (isFirstRender && !_sum) {
			setFieldValue(
				"ProfitsInLieuOfSalaryUnderSection17_3",
				values?.ProfitsInLieuOfSalaryUnderSection17_3 || ""
			);
		} else if (!isFirstRender) {
			setFieldValue("ProfitsInLieuOfSalaryUnderSection17_3", _sum);
		} else if (isFirstRender && _sum) {
			setFieldValue("ProfitsInLieuOfSalaryUnderSection17_3", _sum);
		}
	}, [currentTime]);

	return (
		<>
			<Card className="more-info-card shadow-none mb-5">
				<Card.Header>
					<Card.Title>Break up of Profits in lieu under section 17(3)</Card.Title>
				</Card.Header>
				<Card.Body>
					<section>
						<FieldArray name="ProfitsInLieuDescriptions">
							{({ push, replace, remove, form }) => (
								<>
									{values?.ProfitsInLieuDescriptions &&
									values?.ProfitsInLieuDescriptions?.length > 0
										? values?.ProfitsInLieuDescriptions.map((_, index) => (
												<section className="mb-3 mb-sm-0" key={`salary-allowance-${index}`}>
													<Salary173Form
														index={index}
														form={form}
														onRemove={(itemIdx: number) => {
															if (values.ProfitsInLieuDescriptions.length === 1) {
																replace(itemIdx, ["", ""]);
															} else {
																remove(itemIdx);
															}

															setCurrentTime(new Date().getTime());
														}}
														handleAmountInc={() => setCurrentTime(new Date().getTime())}
													/>
												</section>
										  ))
										: null}
									<PreventEditComponent>
										<Button variant="link" className="btn-add-link" onClick={() => push(["", ""])}>
											<FASvgIcon icon={faCirclePlus} />
											Add more
										</Button>
									</PreventEditComponent>
								</>
							)}
						</FieldArray>
					</section>
				</Card.Body>
				<Card.Footer>
					<p className="total-content">
						<span className="total-label">Total:</span>
						<span className="total-amount">
							<PriceDisplay price={sum} />
						</span>
					</p>
				</Card.Footer>
			</Card>
		</>
	);
};

type Props = {
	index: number;
	form: FormikProps<IAddIncomeForm>;
	onRemove: (itemIdx: number) => void;
	handleAmountInc: () => void;
};

const Salary173Form: React.FC<Props> = (props: Props) => {
	const { index, onRemove, form, handleAmountInc } = props;

	const { setFieldValue, handleBlur, values, touched, errors } = form;

	return (
		<>
			<Row className="align-items-center">
				<Col sm={6}>
					<SelectInputField
						placeholder="Select other"
						name={`ProfitsInLieuDescriptions.${index}[0]`}
						label="Description"
						isOptionArrayOfObject={false}
						options={ProfitsInLieuOfSalary}
						value={[values.ProfitsInLieuDescriptions[index][0]]}
						onChange={(selectedStatus) => {
							if (selectedStatus) {
								setFieldValue(`ProfitsInLieuDescriptions.${index}[0]`, selectedStatus.value);
							} else {
								setFieldValue(`ProfitsInLieuDescriptions.${index}[0]`, undefined);
							}
						}}
						onBlur={handleBlur(`ProfitsInLieuDescriptions.${index}[0]`)}
						containerClasses={
							Boolean(getIn(errors, `ProfitsInLieuDescriptions.${index}[0]`)) &&
							getIn(touched, `ProfitsInLieuDescriptions.${index}[0]`)
						}
						errorMsg={getIn(errors, `ProfitsInLieuDescriptions.${index}[0]`)}
					/>
				</Col>
				<Col sm>
					<NumericInputField
						showLeftIcon={true}
						placeholder=""
						name={`ProfitsInLieuDescriptions.${index}[1]`}
						label="Amount"
						value={values.ProfitsInLieuDescriptions[index][1]}
						onBlur={handleBlur(`ProfitsInLieuDescriptions.${index}[1]`)}
						onChange={(value) => {
							setFieldValue(`ProfitsInLieuDescriptions.${index}[1]`, value);
							handleAmountInc();
						}}
						isInvalid={
							Boolean(getIn(errors, `ProfitsInLieuDescriptions.${index}[1]`)) &&
							getIn(touched, `ProfitsInLieuDescriptions.${index}[1]`)
						}
						errorMsg={getIn(errors, `ProfitsInLieuDescriptions.${index}[1]`)}
					/>
				</Col>
				<PreventEditComponent>
					<Col sm={"auto"} className="ps-0">
						<Button
							variant="transparent"
							className="text-greyDark icon-only d-none d-sm-flex"
							onClick={() => onRemove(index)}
						>
							<FASvgIcon icon={faTrash} />
						</Button>
						<Button
							variant="transparent"
							className="btn-delete-full d-sm-none"
							onClick={() => onRemove(index)}
						>
							<FASvgIcon icon={faTrash} />
							<span>Delete</span>
						</Button>
					</Col>
				</PreventEditComponent>
			</Row>
			{index !== values.ProfitsInLieuDescriptions.length - 1 && (
				<hr className="d-block d-sm-none my-4" />
			)}
		</>
	);
};
